/**
 * 入口：挂载 + 全局手势拦截 + PWA 注册。
 *
 * 注意 viewport 里**没有** user-scalable=no（红线 #56）—— 防误触放大靠这里的
 * dblclick preventDefault 加 CSS 的 touch-action: manipulation，
 * 低视力用户的双指缩放仍然可用。
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import AppRoot from './AppRoot.js'
import { installViewport } from './hooks/useViewportMode.js'
import { useLocalSessionStore } from './local/localSession.js'
import { setupPwa, type PwaController } from './pwa/register.js'
import { readReloadSnapshot } from './pwa/reloadSnapshot.js'
import { isSafeToReload } from './pwa/updateIdle.js'
import { useUpdateStore } from './pwa/updateStore.js'
import { useUiStore } from './store/index.js'

/** PWA shortcut 的深链接 → 初始视图 */
function initialViewFromPath(pathname: string): 'lobby' | 'table' {
  return pathname.startsWith('/table') ? 'table' : 'lobby'
}

function installGestureGuards(): void {
  // Double taps are guarded; pinch zoom remains available.
  const prevent = (e: Event): void => e.preventDefault()
  // Keep pinch zoom available for low-vision players; touch-action handles double taps.
  // 双击放大：touch-action 已挡住大部分，这条兜底 iOS 上带 <a>/<button> 的区域
  document.addEventListener('dblclick', prevent, { passive: false })
  // 长按弹出系统菜单会盖住行动按钮；.selectable 区域（房间码）放行
  document.addEventListener(
    'contextmenu',
    (e) => {
      if (e.target instanceof Element && e.target.closest('.selectable') !== null) return
      e.preventDefault()
    },
    { passive: false },
  )
}

/**
 * 失焦即遮（§11.8 第 5 条）：切后台、锁屏、来电都不能把底牌留在屏幕上。
 *
 * ★ 只在**本地传手机**模式生效。联机模式下每人一台设备，切个应用回来还得长按揭牌
 *   纯属折磨；更糟的是桌面浏览器切标签页就会触发 blur，回来永远是一块纯色遮罩
 *   （揭牌只在 RoomScreen 挂载时做一次）。红线 #12 针对的是「同一块屏幕换人看」，
 *   联机模式下这个前提不存在。
 */
function installPeekGuards(): void {
  const veilNow = (): void => {
    if (useLocalSessionStore.getState().status === 'idle') return
    if (useUiStore.getState().veiled) return
    useUiStore.getState().veil('blur')
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') veilNow()
  })
  globalThis.addEventListener('blur', veilNow)
  globalThis.addEventListener('pagehide', veilNow)
}

const container = document.getElementById('root')
if (container === null) throw new Error('#root 不存在')

useUiStore.getState().setView(initialViewFromPath(globalThis.location.pathname))
installGestureGuards()
installPeekGuards()
// 视口单位兜底（微信工具栏）+ 形态档跟踪（折叠机展开/折叠）。
// 必须在首帧之前装：data-viewport 决定平板档的版面，晚一帧就会先画一帧手机档再跳。
installViewport()

createRoot(container).render(
  <StrictMode>
    <AppRoot />
  </StrictMode>,
)

// 红线 #57：牌局中途绝不**自动**刷新。什么时候算「空闲」按场景分，见 pwa/updateIdle.ts。
// 等待期间右上角挂一枚「新版本」通知，用户可以自己点「立即更新」（session/UpdateNotice.tsx）。
let pwa: PwaController | null = null
pwa = setupPwa({
  isIdle: () => isSafeToReload(readReloadSnapshot()),
  onWaiting() {
    useUpdateStore.getState().markWaiting(() => pwa?.applyUpdate())
  },
})
